import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import styles from './ConfirmDialog.module.scss';

// Centered confirmation dialog. Use it for every "are you sure?" step so the
// prompt is always in view, instead of rendering a confirm panel elsewhere on
// the page where it may be scrolled out of sight.
function ConfirmDialog({
  open,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busyLabel = 'Working…',
  tone = 'danger',
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}) {
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = event => {
      if (event.key === 'Escape' && !busy) {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('navigation-open');
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('navigation-open');
    };
  }, [open, onCancel, busy]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  const Icon = tone === 'danger' ? AlertTriangle : HelpCircle;

  return createPortal(
    <div className={styles.layer} role="presentation">
      <button
        type="button"
        className={styles.backdrop}
        onClick={onCancel}
        disabled={busy}
        aria-label="Cancel"
      />
      <div
        className={`${styles.dialog} ${styles[tone] || ''}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
      >
        <div className={styles.content}>
          <span className={styles.icon}>
            <Icon size={20} />
          </span>
          <div className={styles.text}>
            <h2 id="confirm-dialog-title">{title}</h2>
            {description && <p>{description}</p>}
            {children}
          </div>
        </div>
        <footer className={styles.footer}>
          <button type="button" className={styles.cancel} onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={styles.confirm}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
            autoFocus
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}

export default ConfirmDialog;
